import { useState } from "react";
import { Navigate } from "react-router-dom";

import { Button, Card, CardBody, Icon, Pill } from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { extractErrorMessage } from "@/services/axiosConfig";
import {
  useConfirmPhoneVerification,
  useSendEmailVerification,
  useSendPhoneVerification,
} from "@/hooks/useVerification";
import "./LoginPage.css";
import "./VerifyAccountPage.css";

export function VerifyAccountPage() {
  const { user, refresh } = useAuth();

  const sendEmail = useSendEmailVerification();
  const sendPhone = useSendPhoneVerification();
  const confirmPhone = useConfirmPhoneVerification();

  const [emailSent, setEmailSent] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);

  const [phoneSent, setPhoneSent] = useState(false);
  const [phoneErr, setPhoneErr] = useState<string | null>(null);
  const [code, setCode] = useState("");

  if (!user) return <Navigate to="/login" replace />;

  const emailPending = !user.emailVerified;
  const phonePending = user.hasPhone && !user.phoneVerified;
  const pending = emailPending || phonePending;

  if (!pending) return <Navigate to="/" replace />;

  async function onSendEmail() {
    setEmailErr(null);
    try {
      await sendEmail.mutateAsync();
      setEmailSent(true);
    } catch (err) {
      setEmailErr(extractErrorMessage(err));
    }
  }

  async function onSendPhone() {
    setPhoneErr(null);
    try {
      await sendPhone.mutateAsync();
      setPhoneSent(true);
    } catch (err) {
      setPhoneErr(extractErrorMessage(err));
    }
  }

  async function onConfirmPhone() {
    setPhoneErr(null);
    try {
      await confirmPhone.mutateAsync(code.trim());
      await refresh();
      setCode("");
    } catch (err) {
      setPhoneErr(extractErrorMessage(err));
    }
  }

  return (
    <div className="verify-shell">
      <div className="verify-inner">
        <header className="verify-head">
          <h1 className="login-title">Verifique sua conta</h1>
          <p className="login-sub" style={{ margin: 0 }}>
            Confirme seu e-mail e telefone para liberar o acesso à plataforma.
          </p>
        </header>

        <Card className="verify-card">
          <CardBody>
            <div className="verify-row">
              <div className="verify-row-icon">
                <Icon name="mail" size={18} />
              </div>
              <div className="verify-row-body">
                <div className="verify-row-title">
                  <span>E-mail</span>
                  {user.emailVerified ? (
                    <Pill tone="success">verificado</Pill>
                  ) : (
                    <Pill tone="warning">pendente</Pill>
                  )}
                </div>
                <p className="verify-row-text">{user.email}</p>
                {emailPending && (
                  <>
                    <p className="verify-row-text">
                      Enviamos um link de verificação ao seu e-mail. Clique no link para confirmar.
                    </p>
                    {emailErr && <div className="login-error">{emailErr}</div>}
                    {emailSent && (
                      <p className="verify-ok">E-mail de verificação enviado.</p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      iconLeft="send"
                      onClick={onSendEmail}
                      disabled={sendEmail.isPending}
                    >
                      {sendEmail.isPending ? "Enviando…" : "Reenviar e-mail de verificação"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {user.hasPhone && (
          <Card className="verify-card">
            <CardBody>
              <div className="verify-row">
                <div className="verify-row-icon">
                  <Icon name="phone" size={18} />
                </div>
                <div className="verify-row-body">
                  <div className="verify-row-title">
                    <span>Telefone</span>
                    {user.phoneVerified ? (
                      <Pill tone="success">verificado</Pill>
                    ) : (
                      <Pill tone="warning">pendente</Pill>
                    )}
                  </div>
                  {phonePending && (
                    <>
                      <p className="verify-row-text">
                        Enviaremos um código por SMS. Digite-o abaixo para confirmar.
                      </p>
                      {phoneErr && <div className="login-error">{phoneErr}</div>}
                      {!phoneSent ? (
                        <Button
                          variant="outline"
                          size="sm"
                          iconLeft="send"
                          onClick={onSendPhone}
                          disabled={sendPhone.isPending}
                        >
                          {sendPhone.isPending ? "Enviando…" : "Enviar código"}
                        </Button>
                      ) : (
                        <div className="verify-otp">
                          <p className="verify-ok">Código enviado por SMS.</p>
                          <div className="verify-otp-row">
                            <input
                              className="verify-otp-input"
                              value={code}
                              onChange={(e) => setCode(e.target.value)}
                              inputMode="numeric"
                              placeholder="Código"
                              autoComplete="one-time-code"
                            />
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={onConfirmPhone}
                              disabled={confirmPhone.isPending || code.trim().length === 0}
                            >
                              {confirmPhone.isPending ? "Confirmando…" : "Confirmar"}
                            </Button>
                          </div>
                          <button
                            type="button"
                            className="verify-resend"
                            onClick={onSendPhone}
                            disabled={sendPhone.isPending}
                          >
                            Reenviar código
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}
